/**
 * InviteModal Component - Guest invitation dialog
 *
 * Features:
 * - Generate shareable link
 * - Copy link to clipboard
 * - Send email invite (opens default email client)
 * - QR code for mobile
 * - Pending invites list
 */

import React, { useState, useEffect } from 'react';
import { StudioSession } from '../lib/session';
import { openEmailInvite, copyInviteToClipboard } from '../lib/email-invite';

interface InviteModalProps {
  session: StudioSession;
  onClose: () => void;
}

interface PendingInvite {
  email: string;
  name: string;
  sentAt: number;
  status: 'pending' | 'joined';
}

export function InviteModal({ session, onClose }: InviteModalProps) {
  const [inviteLink, setInviteLink] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Generate invite link on mount
  useEffect(() => {
    async function generateLink() {
      try {
        const link = await session.generateInviteLink();
        setInviteLink(link);
      } catch (err) {
        console.error('[InviteModal] Failed to generate link:', err);
        setError('Failed to generate invite link');
      } finally {
        setIsLoading(false);
      }
    }

    generateLink();
  }, [session]);

  // Send email invite via mailto: link (opens default email client)
  const sendEmailInvite = (e: React.FormEvent) => {
    e.preventDefault();

    if (!emailInput.trim()) {
      setError('Please enter an email address');
      return;
    }

    if (!isValidEmail(emailInput)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!inviteLink) {
      setError('Invite link not ready yet. Please wait.');
      return;
    }

    setError(null);

    // Get session details from config
    const config = session.getConfig?.();
    const sessionTitle = config?.title || 'MyPodOps Studio Session';
    const hostName = config?.hostName || 'Your host';

    // Use the email-invite module to open the default email client
    const success = openEmailInvite({
      sessionTitle,
      hostName,
      inviteUrl: inviteLink,
      additionalNotes: nameInput.trim() ? `Looking forward to having ${nameInput.trim()} join!` : undefined
    }, emailInput.trim());

    if (!success) {
      setError('Failed to open email client. Please copy the link instead.');
      return;
    }

    // Add to pending invites (local tracking only)
    setPendingInvites(prev => [
      ...prev,
      {
        email: emailInput.trim(),
        name: nameInput.trim() || 'Guest',
        sentAt: Date.now(),
        status: 'pending'
      }
    ]);

    // Clear inputs
    setEmailInput('');
    setNameInput('');
  };

  // Quick send - opens email client without needing to fill form
  const handleQuickEmailInvite = () => {
    if (!inviteLink) {
      setError('Invite link not ready yet. Please wait.');
      return;
    }

    setError(null);

    const config = session.getConfig?.();
    const sessionTitle = config?.title || 'MyPodOps Studio Session';
    const hostName = config?.hostName || 'Your host';

    // Open email client with blank recipient (user fills it in)
    openEmailInvite({
      sessionTitle,
      hostName,
      inviteUrl: inviteLink
    });
  };

  // Enhanced copy to clipboard using the email-invite module
  const handleCopyLink = async () => {
    const success = await copyInviteToClipboard(inviteLink);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      setError('Failed to copy link');
    }
  };

  // Email validation
  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Format time ago
  const formatTimeAgo = (timestamp: number): string => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="invite-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>👥 Invite Guests</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Content */}
        <div className="modal-content">
          {/* Share Link Section */}
          <div className="invite-section">
            <h3>📎 Share Link</h3>
            <p className="section-desc">Anyone with this link can join your session</p>

            {isLoading ? (
              <div className="loading-link">
                <span className="spinner-small"></span>
                Generating link...
              </div>
            ) : (
              <div className="link-container">
                <input
                  type="text"
                  className="link-input"
                  value={inviteLink}
                  readOnly
                />
                <div className="link-actions">
                  <button
                    className={`copy-btn ${copied ? 'copied' : ''}`}
                    onClick={handleCopyLink}
                  >
                    {copied ? '✓ Copied!' : '📋 Copy'}
                  </button>
                  <button
                    className="quick-email-btn"
                    onClick={handleQuickEmailInvite}
                    title="Open email client with invite"
                  >
                    ✉️ Email
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Email Invite Section */}
          <div className="invite-section">
            <h3>📧 Email Invite</h3>
            <p className="section-desc">Opens your email client with invite details</p>

            <form className="email-form" onSubmit={sendEmailInvite}>
              <div className="form-row">
                <input
                  type="text"
                  className="name-input"
                  placeholder="Guest name (optional)"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                />
              </div>
              <div className="form-row">
                <input
                  type="email"
                  className="email-input"
                  placeholder="Email address"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                />
                <button
                  type="submit"
                  className="send-btn"
                  disabled={!emailInput.trim() || !inviteLink}
                >
                  📨 Compose Email
                </button>
              </div>
            </form>
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-message">
              <span>⚠️</span> {error}
            </div>
          )}

          {/* Pending Invites */}
          {pendingInvites.length > 0 && (
            <div className="invite-section">
              <h3>⏳ Pending Invites</h3>
              <div className="pending-list">
                {pendingInvites.map((invite, index) => (
                  <div key={index} className="pending-invite">
                    <div className="invite-info">
                      <span className="invite-name">{invite.name}</span>
                      <span className="invite-email">{invite.email}</span>
                    </div>
                    <div className="invite-meta">
                      <span className={`invite-status ${invite.status}`}>
                        {invite.status === 'pending' ? '⏳ Pending' : '✅ Joined'}
                      </span>
                      <span className="invite-time">{formatTimeAgo(invite.sentAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <p className="footer-note">
            💡 Guests will enter a green room where you can admit them
          </p>
        </div>
      </div>
    </div>
  );
}
